import React, { useMemo } from 'react'
import { styled } from '@mui/material/styles'
import { Card, CardMedia, CardContent, Typography } from '@mui/material'
import { productUrl } from '@helpers/route'
import { capitalizeWords } from '@helpers/string-helper'
import { useTest } from '@hooks/useTest'
import { addToDataLayer } from '@helpers/google-tag-manager'
import BaseSwiper from '../../../../@rtg2022/components/organisms/BaseSwiper/BaseSwiper'
import { useBaseCollectionTileContext } from '../BaseCollectionTileContext'

const LabelWrapper = styled('div')(() => ({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
}))

const BedStyleLabel = styled('span')(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.primary.dark,
  fontWeight: 600,
  lineHeight: 'normal',
  textTransform: 'uppercase',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
  },
}))

const OptionsText = styled('span')(({ theme }) => ({
  fontSize: '12px',
  color: '#707070',
  fontWeight: 400,
  lineHeight: 'normal',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
  },
}))

const StyledCard = styled(Card)(() => ({
  width: '100%',
  backgroundColor: 'transparent',
  borderRadius: 0,
}))

const StyledCardMedia = styled(CardMedia)(() => ({
  boxSizing: 'border-box',
  width: '100%',
  padding: '16px',
  backgroundColor: '#F5F5F5',
  aspectRatio: '5 / 3',
  objectFit: 'contain',
}))

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: '8px 0',
  '&:last-child': {
    paddingBottom: '8px',
  },
}))

const StyledSwiperWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  boxSizing: 'border-box',
  [theme.breakpoints.up('md')]: {
    paddingBottom: '24px',
  },
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  color: theme.palette.text.primary,
  textTransform: 'capitalize',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
    fontWeight: 500,
  },
}))

const determineLabelAndPriorityItem = collection => {
  const { roomSlots, category } = collection ?? {}
  if (category === 'bedroom') {
    const hasBunkBeds = roomSlots?.['bunk bed']
    const hasLoftBeds = roomSlots?.['loft bed']

    let priorityItem = null
    if (hasBunkBeds) {
      priorityItem = 'bunk bed'
    } else if (hasLoftBeds) {
      priorityItem = 'loft bed'
    }

    return {
      label: hasBunkBeds || hasLoftBeds ? 'Bed & Bunk Styles:' : 'Bed Styles:',
      priorityItem,
    }
  }
  return { label: 'Styles:', priorityItem: null }
}

const StyleSwiper = () => {
  const { collection } = useBaseCollectionTileContext()
  const { label, priorityItem } = determineLabelAndPriorityItem(collection)
  const { roomSlots, title } = collection ?? {}
  const { generateTestId } = useTest()
  // Moves roomSlots into an array, and makes sure the priority item is in the 2nd position
  const roomSlotsArray = useMemo(() => {
    if (!roomSlots) return []

    const slotsArray = Object.entries(roomSlots)
    const priorityIndex = priorityItem && slotsArray.findIndex(slot => slot[0] === priorityItem)
    if (priorityIndex > -1 && priorityIndex !== 1 && slotsArray.length > 1) {
      const prioritySlot = slotsArray.splice(priorityIndex, 1)[0]
      slotsArray.splice(1, 0, prioritySlot)
    }
    return slotsArray
  }, [priorityItem, roomSlots])

  if (roomSlotsArray.length === 0) return null

  const numOfSlots = roomSlotsArray.length

  const slides = roomSlotsArray
    .map(([slotName, value]) => {
      const { itemImage, _id, route } = value
      if (!itemImage || !_id || !slotName) return null
      return (
        <a
          href={route ? `${productUrl(route)}` : '#'}
          key={_id}
          aria-label={`View ${slotName} details`}
          tabIndex="0"
          data-testid={generateTestId('collection-tile', 'style-slot')}
          onClick={() => {
            addToDataLayer('click', 'CLP', 'Style Tile Click', title, slotName)
          }}
        >
          <StyledCard elevation={0} key={_id}>
            <StyledCardMedia
              component="img"
              image={itemImage}
              alt={capitalizeWords(slotName)}
              title={capitalizeWords(slotName)}
            />
            <StyledCardContent>
              {slotName !== 'no style' && (
                <StyledTypography variant="body1">{capitalizeWords(slotName)}</StyledTypography>
              )}
            </StyledCardContent>
          </StyledCard>
        </a>
      )
    })
    .filter(Boolean)

  return (
    <StyledSwiperWrapper data-testid={generateTestId('collection-tile', 'style-swiper')}>
      <LabelWrapper>
        {numOfSlots && (
          <>
            <BedStyleLabel>{label}</BedStyleLabel>
            <OptionsText>
              {numOfSlots} Option{numOfSlots === 1 ? '' : 's'}
            </OptionsText>
          </>
        )}
      </LabelWrapper>
      <div style={{ zIndex: 1 }}>
        <BaseSwiper
          desktopSlides={slides}
          mobileSlides={slides}
          slidesPerViewDesktop={2.6}
          slidesPerViewMobile={2.3}
          spaceBetween={8}
          hideScrollbar
          navigationVariant="variant2"
          aria-label={`${label} carousel with ${numOfSlots} options`}
        />
      </div>
    </StyledSwiperWrapper>
  )
}

export default StyleSwiper
